/* eslint-disable @typescript-eslint/no-magic-numbers */

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  ViewChild,
} from '@angular/core';
import {
  asset,
  drawTeeth,
  getJawFilter,
  isReverse,
  Jaw,
  PERIO_CONTAINER_HEIGHT,
  PERIO_CONTAINER_WIDTH,
  PERIO_JAW_SIDE_LABEL_WIDTH,
  PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
  PocketDepthRef,
  Svg,
  SvgGroup,
  Tooth,
  ToothWithLayers,
  TOOTH_PERIO_HEIGHT,
  ToothCoordinates,
  ToothSide,
} from '@libs/ui-lib';
import { ConfigService, ScreenSize } from '@libs/shared-services';
import { select } from 'd3-selection';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, pairwise, startWith, Subject } from 'rxjs';
import { isNotNullOrUndefined } from '@libs/utils';
import { TEETH_TRANSLATE_Y } from '../teeth/teeth.constants';
import { calculateCoordinates } from './perio-jaw-coordinates';
import { PerioJaw, PerioJawConfig } from './perio-jaw.interface';
import { drawBackgroundLines, drawPerioTreatments, drawRulerLabels, drawSideLabels } from './perio-jaw-drawing';
import { ANIMATION_DURATION } from './perio-jaw.constants';

// Offset to properly show the tooth with pocket depth
const TOOTH_FOCUS_OFFSET = 6;
const TOOTH_FOCUS_OFFSET_LEFT = 35;

@Component({
  selector: 'lib-perio-jaw',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './perio-jaw.component.html',
  styleUrl: './perio-jaw.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioJawComponent implements AfterViewInit {
  private readonly configService = inject(ConfigService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly screenSize: ScreenSize = 'xl';
  private readonly pocketDepthRefs: PocketDepthRef[] = [];

  public readonly selectedTooth = input<Tooth | null>();
  public readonly teeth = input<ToothWithLayers[]>([]);
  public readonly missingTeeth = input<number[]>([]);
  public readonly activeSide = input<ToothSide>('buccal');
  public readonly activeJaw = input<Jaw>('upper');
  public readonly showRulerLabels = input<boolean>(true);
  public readonly showSideLabels = input<boolean>(false);
  public readonly isFirstTooth = input<boolean>(false);
  public readonly isLastTooth = input<boolean>(false);
  public readonly isLeftSide = input<boolean>(false);

  // We do this to avoid unnecessary re-renders when only position or side changes
  private readonly currentlySelectedTooth = computed(
    () => {
      return { ...this.selectedTooth(), side: this.activeSide() };
    },
    { equal: (prev, curr) => prev.position === curr.position && prev.side === curr.side },
  );
  private readonly mainSvg = signal<Svg | null>(null);

  private readonly activeSide$ = toObservable(this.activeSide);
  private readonly selectedTooth$ = toObservable(this.selectedTooth);
  private readonly jawSvgMap = new Map<string, Svg>();
  private readonly sideLabelsMap = new Map<string, SvgGroup>();

  private readonly scrollToTooth$ = new Subject<number>();

  @ViewChild('container', { static: true }) public containerRef!: ElementRef;
  private readonly rulerLabels = viewChild<ElementRef<HTMLElement>>('rulerLabels');

  public teethAssetBasePath = this.configService.config()?.teethAssetBasePath ?? '';

  private isFirstRender = true;
  private lastRedrawnSide: ToothSide | null = null;

  constructor() {
    this.scrollToTooth$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((toothPosition) => {
      this.scrollToTooth(
        toothPosition || 1,
        untracked(this.activeJaw),
        !this.isFirstRender, // Pass false on first render, true afterward
        untracked(this.isFirstTooth),
        untracked(this.isLastTooth),
      );
      this.hideOrShowSideLabels();
      this.updateSideLabels(untracked(this.selectedTooth)?.jaw || untracked(this.activeJaw));

      if (this.isFirstRender && this.mainSvg()) {
        this.isFirstRender = false;
      }
    });

    effect(() => {
      this.scrollToTooth$.next(this.currentlySelectedTooth()?.position ?? 1);
    });

    effect(() => {
      if (this.currentlySelectedTooth() && this.mainSvg()) {
        this.updatePerioTreatments();
      }
    });

    effect(() => {
      if (this.missingTeeth()) {
        this.resetAndRenderJawInReactiveContext();
      }
    });

    // Explicitly redraw on side change (buccal <-> lingual).
    // This ensures the correct assets/coordinates are rebuilt, not just opacity/perio updates.
    effect(() => {
      const side = this.activeSide();
      if (this.lastRedrawnSide === null) {
        this.lastRedrawnSide = side;
        return;
      }
      if (this.lastRedrawnSide !== side) {
        this.lastRedrawnSide = side;
        this.resetAndRenderJawInReactiveContext();
      }
    });
  }

  public ngAfterViewInit(): void {
    combineLatest([this.selectedTooth$, this.activeSide$])
      .pipe(
        startWith([this.teeth()[0], this.activeSide()] as [Tooth | null | undefined, ToothSide]),
        takeUntilDestroyed(this.destroyRef),
        pairwise(),
        map(([[oldTooth, oldSide], [newTooth, newSide]]) => {
          if (oldTooth?.position === newTooth?.position && oldSide === newSide) {
            return null;
          }
          return newTooth;
        }),
        filter((tooth) => isNotNullOrUndefined(tooth)),
      )
      .subscribe((tooth) => {
        this.scrollToTooth(
          tooth?.position ?? 1,
          this.activeJaw(),
          !this.isFirstRender, // Pass false on first render, true afterward
          this.isFirstTooth(),
          this.isLastTooth(),
        );
      });
  }

  public resetAndRenderJaw(): void {
    this.isFirstRender = true;
    this.jawSvgMap.clear();
    this.sideLabelsMap.clear();
    this.mainSvg()?.remove();
    this.mainSvg.set(null);
    this.renderJaw();
    this.hideOrShowSideLabels();
  }

  private resetAndRenderJawInReactiveContext(): void {
    untracked(() => {
      if (this.mainSvg()) {
        this.resetAndRenderJaw();
      } else {
        this.renderJaw();
      }
    });
  }

  private getJawTeeth(teeth: Tooth[], jaw: Jaw): Tooth[] {
    const jawFilter = getJawFilter(jaw);
    return teeth.filter((element) => jawFilter(element));
  }

  private getJawCoordinates(jaw: Jaw, toothCoords: ToothCoordinates): PerioJaw {
    switch (jaw) {
      case 'upper': {
        return {
          buccalTeethCoordinates: toothCoords.upperJawBuccalTeethCoordinates,
          buccalPeriosCoordinates: toothCoords.upperJawBuccalPeriosCoordinates,
          lingualTeethCoordinates: toothCoords.upperJawLingualTeethCoordinates,
          lingualPeriosCoordinates: toothCoords.upperJawLingualPeriosCoordinates,
          buccalCejPosition: 'lower',
          lingualCejPosition: 'lower',
          buccalCirclesPosition: 'bottom',
          lingualCirclesPosition: 'bottom',
          mobilityPosition: 'below',
          jaw: 'upper',
        };
      }
      case 'lower': {
        return {
          buccalTeethCoordinates: toothCoords.lowerJawBuccalTeethCoordinates,
          buccalPeriosCoordinates: toothCoords.lowerJawBuccalPeriosCoordinates,
          lingualTeethCoordinates: toothCoords.lowerJawLingualTeethCoordinates,
          lingualPeriosCoordinates: toothCoords.lowerJawLingualPeriosCoordinates,
          buccalCejPosition: 'upper',
          lingualCejPosition: 'upper',
          buccalCirclesPosition: 'top',
          lingualCirclesPosition: 'top',
          mobilityPosition: 'above',
          jaw: 'lower',
        };
      }
    }
  }

  private getSideLabelTranslationString(jaw: Jaw): { upperText: string; lowerText: string } {
    const upperJawUpperTextTranslationString = 'perio_exam_upper_jaw_buccal_label';
    const upperJawLowerTextTranslationString = 'perio_exam_upper_jaw_lingual_label';
    const lowerJawUpperTextTranslationString = 'perio_exam_lower_jaw_lingual_label';
    const lowerJawLowerTextTranslationString = 'perio_exam_lower_jaw_buccal_label';

    const upperText = this.translateService.instant(
      jaw === 'upper' ? upperJawUpperTextTranslationString : lowerJawUpperTextTranslationString,
    );
    const lowerText = this.translateService.instant(
      jaw === 'upper' ? upperJawLowerTextTranslationString : lowerJawLowerTextTranslationString,
    );

    return { upperText, lowerText };
  }

  private hideOrShowSideLabels(): void {
    this.sideLabelsMap.forEach((svg, key) => {
      if (key === `${untracked(this.activeJaw)}-${untracked(this.activeSide)}` || untracked(this.isLastTooth)) {
        svg.attr('opacity', 0);
      } else {
        svg.attr('opacity', 1);
      }
    });
  }

  private updateSideLabels(activeJaw: Jaw): void {
    const sideLabelsSvg = this.sideLabelsMap.get('container');

    if (!sideLabelsSvg) {
      return;
    }

    const { upperText, lowerText } = this.getSideLabelTranslationString(activeJaw);

    const sideLabels = sideLabelsSvg.selectAll<SVGTextElement, unknown>('text');

    sideLabels.each((d, i, nodes) => {
      const textElement = select(nodes[i]);
      if (i === 0) {
        textElement.text(upperText);
      } else {
        textElement.text(lowerText);
      }
    });
  }

  private drawSideLabels(svg: Svg | SvgGroup, activeJaw: Jaw, height: number, x?: number): SvgGroup {
    const { upperText, lowerText } = this.getSideLabelTranslationString(activeJaw);

    return drawSideLabels(svg, height, PERIO_JAW_SIDE_LABEL_WIDTH, '#D3DBE2', upperText, lowerText, x);
  }

  private updatePerioTreatments() {
    const jawGroup = this.jawSvgMap.get(`${this.activeJaw()}-${this.activeSide()}`);

    if (!jawGroup) {
      return;
    }

    const jaw = this.activeJaw();
    const filteredTeeth = this.teeth().filter((tooth) => tooth.jaw === jaw);
    const jawTeeth = jaw === 'lower' ? [...filteredTeeth].reverse() : filteredTeeth;

    const teethTranslateY = {
      upper: TEETH_TRANSLATE_Y(this.screenSize, PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL).upper,
      lower: TEETH_TRANSLATE_Y(this.screenSize, PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL).upper,
    };
    const coords = calculateCoordinates(jawTeeth, this.screenSize, teethTranslateY);
    const jawCoords = this.getJawCoordinates(jaw, coords);
    const jawHeight = Number.parseFloat(jawGroup.attr('height'));

    drawPerioTreatments(jawGroup, jawTeeth, this.activeSide(), jawCoords, jawHeight, this.screenSize, true);
  }

  private readonly getToothAssetPlacement = (jaw: Jaw, side: ToothSide): ((position: number) => string) => {
    const toothAssetPathTop = (position: number) => `${asset(position)}/base/perio-top.png`;
    const toothAssetPathBottom = (position: number) => `${asset(position)}/base/perio-bottom.png`;
    const buccalToothAssetPathPlacement = jaw === 'upper' ? toothAssetPathTop : toothAssetPathBottom;
    const lingualToothAssetPathPlacement = jaw === 'upper' ? toothAssetPathBottom : toothAssetPathTop;
    if (side === 'buccal') {
      return buccalToothAssetPathPlacement;
    }
    return lingualToothAssetPathPlacement;
  };

  private readonly drawJaw = (
    jawGroup: Svg,
    filteredTeeth: Tooth[],
    jawCoords: PerioJaw,
    jawHeight: number,
    perioJawConfig: PerioJawConfig,
    onlyPerioTreatments = false,
  ) => {
    if (!onlyPerioTreatments) {
      // Set up tooth asset paths

      const filterTeeth = (tooth: ToothWithLayers) => !this.missingTeeth().includes(tooth.position);

      // Draw background lines
      drawBackgroundLines(jawGroup, jawCoords.buccalTeethCoordinates, jawCoords.buccalCejPosition, perioJawConfig.jaw === 'lower');
      drawBackgroundLines(jawGroup, jawCoords.lingualTeethCoordinates, jawCoords.lingualCejPosition, perioJawConfig.jaw === 'lower');

      // Draw buccal and lingual teeth
      drawTeeth({
        svg: jawGroup,
        teeth: filteredTeeth,
        teethCoordinates: jawCoords.buccalTeethCoordinates,
        screenSize: this.screenSize,
        drawTreatments: false,
        height: TOOTH_PERIO_HEIGHT(this.screenSize),
        toothTransform: (position) => (isReverse(position) ? 'scale(-1, 1)' : 'scale(1)'),
        toothAssetPath: this.getToothAssetPlacement(perioJawConfig.jaw, 'buccal'),
        teethAssetBasePath: this.teethAssetBasePath,
        shouldDrawTooth: filterTeeth,
        opacity: this.activeSide() === 'buccal' ? 1 : 0.5,
      });

      drawTeeth({
        svg: jawGroup,
        teeth: filteredTeeth,
        teethCoordinates: jawCoords.lingualTeethCoordinates,
        screenSize: this.screenSize,
        drawTreatments: false,
        height: TOOTH_PERIO_HEIGHT(this.screenSize),
        toothTransform: (position) => (isReverse(position) ? 'scale(-1, -1)' : 'scale(1, -1)'),
        toothAssetPath: this.getToothAssetPlacement(perioJawConfig.jaw, 'lingual'),
        teethAssetBasePath: this.teethAssetBasePath,
        shouldDrawTooth: filterTeeth,
        opacity: this.activeSide() === 'lingual' ? 1 : 0.5,
      });
    }

    drawPerioTreatments(jawGroup, filteredTeeth, perioJawConfig.side, jawCoords, jawHeight, this.screenSize, true);
  };

  private renderJaw() {
    const containerElement = this.containerRef.nativeElement;
    const rulerLabels = this.rulerLabels();
    containerElement.innerHTML = '';

    const jawBaseContainerHeight = PERIO_CONTAINER_HEIGHT(this.screenSize, PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL);
    const jawBaseContainerWidth = PERIO_CONTAINER_WIDTH(this.screenSize);
    const containerWidth = jawBaseContainerWidth * 1.5;
    const jawHeight = jawBaseContainerHeight / 1.5;

    const containerSvg = select(containerElement)
      .append('svg')
      .attr('style', `overflow: ${this.isLeftSide() ? 'visible' : 'hidden'}`)
      .attr('width', containerWidth)
      .attr('viewBox', `0 0 ${jawBaseContainerWidth} ${jawHeight}`);

    const mainSvg = containerSvg
      .append('svg')
      .attr('style', `overflow: ${this.isLeftSide() ? 'visible' : 'hidden'}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${jawBaseContainerWidth} ${jawHeight}`);

    // Create main SVG container for all jaw configurations
    this.mainSvg.set(mainSvg);

    if (this.showRulerLabels() && rulerLabels) {
      const rulerLabelsElement = rulerLabels.nativeElement;
      rulerLabelsElement.innerHTML = '';
      const rulerLabelSvg = select(rulerLabelsElement)
        .append('svg')
        .attr('width', containerWidth)
        .attr('viewBox', `0 0 ${jawBaseContainerWidth} ${jawHeight}`);

      drawRulerLabels(rulerLabelSvg, jawHeight, '#D3DBE2', PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL);
    }

    if (this.showSideLabels()) {
      this.sideLabelsMap.set('container', this.drawSideLabels(containerSvg, this.activeJaw(), jawHeight));
    }

    // Single rendered config (fixed visual orientation)
    const jaw = untracked(this.activeJaw);
    const side = untracked(this.activeSide);
    const perioJawConfig: PerioJawConfig = { jaw, side, reverseTraversalOrder: jaw === 'lower', visualDirection: 'right' };
    [perioJawConfig].forEach((config, index) => {
      const filteredTeeth = this.getJawTeeth(this.teeth(), config.jaw);

      const jawTeeth = config.reverseTraversalOrder ? [...filteredTeeth].reverse() : filteredTeeth;

      // Use the same y translation to render both jaws as we are using only one position (upper) for both
      const teethTranslateY = {
        upper: TEETH_TRANSLATE_Y(this.screenSize, PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL).upper,
        lower: TEETH_TRANSLATE_Y(this.screenSize, PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL).upper,
      };

      // Initialize coordinate variables for each jaw
      const coords = calculateCoordinates(jawTeeth, this.screenSize, teethTranslateY);

      // Set coordinates based on the jaw
      const jawCoords = this.getJawCoordinates(config.jaw, coords);
      const x = index * jawBaseContainerWidth;
      const jawGroupSide = `${config.jaw}-${config.side}`;
      const jawGroup = mainSvg.append('svg').attr('class', 'jaw-group').attr('data-side', jawGroupSide).attr('x', x);

      this.drawJaw(jawGroup, jawTeeth, jawCoords, jawHeight, config);

      this.sideLabelsMap.set(jawGroupSide, this.drawSideLabels(jawGroup, config.jaw, jawHeight, 35));
      this.jawSvgMap.set(jawGroupSide, jawGroup);
    });
  }

  private getAbsoluteXForLastTooth(jawX: number, jawElement: SVGSVGElement, toothBBox: DOMRect): number {
    const svgBBox = jawElement.getBBox();
    const totalWidth = svgBBox.width;
    return jawX + totalWidth + toothBBox.width;
  }

  public scrollToTooth(toothNumber: number = 1, jaw: Jaw, isAnimated: boolean, isFirstTooth = false, isLastTooth = false): void {
    let absoluteX = 0;
    const mainSvg = this.mainSvg();
    // Rendering now produces a single jaw group. We scroll whatever group exists.
    const jawSvg = this.jawSvgMap.get(`${jaw}-${this.activeSide()}`) ?? this.jawSvgMap.values().next().value;

    if (!jawSvg || !mainSvg) {
      return;
    }

    const tooth = jawSvg.selectAll<SVGImageElement, unknown>('image').filter(function () {
      return select(this).attr('data-tooth-position') === String(toothNumber);
    });

    const toothElement = tooth.node();
    const jawElement = jawSvg.node();

    if (!jawElement) {
      return;
    }

    // Get the tooth's bounding box and position
    const jawX = Number.parseFloat(select(jawElement).attr('x') || '0');
    const toothFocusOffset = this.isLeftSide() ? TOOTH_FOCUS_OFFSET_LEFT : TOOTH_FOCUS_OFFSET;
    const offset = this.showRulerLabels() ? PERIO_JAW_SIDE_LABEL_WIDTH + toothFocusOffset : toothFocusOffset;

    // Get the current viewBox to maintain width and height
    const currentViewBox = mainSvg.attr('viewBox')?.split(' ') || [];
    const viewBoxWidth = Number.parseFloat(currentViewBox[2]);
    const viewBoxHeight = currentViewBox[3];

    // Calculate absolute position in the main SVG coordinates if tooth exists
    if (toothElement) {
      const toothBBox = toothElement?.getBBox();
      absoluteX = jawX + toothBBox.x - offset;
      if (isFirstTooth) {
        absoluteX = -(toothBBox.x + toothBBox.width);
      } else if (isLastTooth) {
        absoluteX = this.getAbsoluteXForLastTooth(jawX, jawElement, toothBBox);
      }
    }

    // Set the viewBox to the calculated x coordinate while maintaining original dimensions
    mainSvg
      .transition()
      .duration(isAnimated ? ANIMATION_DURATION : 0)
      .attr('viewBox', `${absoluteX} 0 ${viewBoxWidth} ${viewBoxHeight}`);
  }
}
